import React, { useMemo } from "react";
import "./App.css";
import { ChakraProvider } from "@chakra-ui/react";
import { createJungleChakraTheme } from "./chakraTheme";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AuthProvider } from "./contextsAndHooks/AuthContext";
import AppRouter from "./AppRouter";
import { SpeedInsights } from "@vercel/speed-insights/react";
import {
  getOpenPanelClientForJungleWebApp,
  initializeOpenPanelForJungleWebApp,
} from "./utils/analytics/openpanel-client";
import { Tolgee, TolgeeProvider } from "@tolgee/react";
import { FormatIcu } from "@tolgee/format-icu";
import { VoiceProvider } from "./voice/react/VoiceProvider";
import { bundledTranslationLoadersByLanguage } from "./i18n/bundledTranslationLoadersByLanguage";
import { markStartupPhase, setStartupProfileReporter } from "./utils/startup-profile";
import { RouteRenderFailureElement } from "./components/RootErrorBoundary";
import { captureAcquisitionAttributionOnAppArrival } from "./utils/attribution-module-loaders";
import { isMarketingSiteOrigin } from "./pages/landing-page/handoff/appHandoff";
import { rememberEmailLinkArrivalAtAppStartup } from "./utils/analytics/email-link-arrival";
import { readFirstUpsellVersion } from "./pages/review-deck-page/first-user-experience/firstUpsellSwitch";
import { finishAnswerFirstSavesWhenTheirAccountIsSignedIn } from "./pages/review-deck-page/first-user-experience/answer-first/answerFirstPendingSave";
import { rememberPushNotificationNewWindowArrivalAtAppStartup } from "./utils/analytics/push-notification-opens";
import { jungleChakraColorModeManager, resolveColorThemeForPath } from "./theme/useJungleColorThemeStore";
import { initialInterfaceLanguage } from "./i18n/interfaceLanguagePreference";
import { useInterfaceLanguage } from "./i18n/interfaceLanguageRuntime";
import { getLocale } from "./i18n/localeRegistry";
import { redactStudyGuideTelemetryUrls } from "./utils/analytics/study-guide-url-privacy";

// Initialize OpenPanel and expose the real SDK instance globally. Tranzmit
// and any other third-party consumers that read window.openpanel expect the
// full OpenPanel class — not our safety
// wrapper — so we expose the underlying SDK instance directly here.
// Internal Jungle code keeps using getOpenPanelClientForJungleWebApp()
// for the try/catch + no-op safety.
//
// Init is a no-op in non-production builds and when VITE_OPENPANEL_CLIENT_ID
// is unset; in those cases window.openpanel will be `null`.
initializeOpenPanelForJungleWebApp();
markStartupPhase("app_module_ready");
setStartupProfileReporter(properties => getOpenPanelClientForJungleWebApp().track("App Startup Measured", properties));
(window as any).openpanel = getOpenPanelClientForJungleWebApp().rawOpenPanelSdkInstanceOrNull;

// Per-ad attribution (contract C2): record the campaign on the arrival URL (and, in the mobile app, its
// install attribution). The arrival is read synchronously here, before the router can rewrite the URL; the
// helpers that store it load after startup, and a profile created on this page waits for them
// (createUserOnFirebase). The marketing site records no touches (its storage is not the app's): it forwards
// the campaign params on the handoff (getForwardedTrackingParams), and this records them on arrival.
if (!isMarketingSiteOrigin()) captureAcquisitionAttributionOnAppArrival();

// An arrival from a SMASHSEND email link (e.g. the abandoned cart flow) is read here for the same reason;
// AppRouter reports it once auth has settled.
if (!isMarketingSiteOrigin()) rememberEmailLinkArrivalAtAppStartup(window.location.search);

// The upsell after the first review (?first_upsell=..., firstUpsellSwitch.ts): read here for the same reason
if (!isMarketingSiteOrigin()) readFirstUpsellVersion();
// ...and the first set a visitor saved on the questions screen reaches their account from any page, even when they
// leave the first review before its save has finished (answerFirstPendingSave.ts)
if (!isMarketingSiteOrigin()) finishAnswerFirstSavesWhenTheirAccountIsSignedIn();
// So is a window the desktop push service worker opened for a tap: it is reported once auth has settled
// (AppRouter), and its two push parameters leave the address before the router reads it.
if (!isMarketingSiteOrigin()) rememberPushNotificationNewWindowArrivalAtAppStartup();

// Initialize Tolgee with bundled local translations only (no CDN/API).
const tolgee = Tolgee().use(FormatIcu()).init({
  language: initialInterfaceLanguage().effectiveLocale,
  staticData: bundledTranslationLoadersByLanguage,
});

// A data router is required for route blockers. The support knowledge-base
// editor uses one so browser back/forward and every in-app navigation respect
// unsaved drafts, not only full-page unloads. The data router also wraps this
// root route in an error boundary; without `errorElement` a route-level render
// failure shows React Router's unstyled default page with no way to recover.
const browserRouter = createBrowserRouter([
  {
    path: "*",
    element: <AppRouter />,
    errorElement: <RouteRenderFailureElement />,
  },
]);

function App() {
  // The theme starts in the color theme this page opens with, so Chakra's first frame is already right;
  // JungleColorThemeSync (in AppRouter) follows every later change.
  const baseTheme = useMemo(
    () => createJungleChakraTheme(undefined, resolveColorThemeForPath(window.location.pathname)),
    []
  );
  const { effectiveLocale } = useInterfaceLanguage();
  const { direction } = getLocale(effectiveLocale);
  // The root theme also reaches Chakra's toast host and every portal without remounting the app.
  const customTheme = useMemo(() => ({ ...baseTheme, direction }), [baseTheme, direction]);

  return (
    <>
      <TolgeeProvider
        tolgee={tolgee}
        // fallback="Loading..." // loading fallback
      >
        {/* <TolgeeLanguageUpdater /> */}
        <ChakraProvider theme={customTheme} colorModeManager={jungleChakraColorModeManager}>
          <AuthProvider>
            <VoiceProvider>
              <RouterProvider router={browserRouter} />
            </VoiceProvider>
          </AuthProvider>
        </ChakraProvider>
      </TolgeeProvider>
      <SpeedInsights beforeSend={redactStudyGuideTelemetryUrls} />
    </>
  );
}

export default App;
